// Billing / income helpers — shared by the Treatment tab, the Operations list
// income column, and the income dashboard.
//
// Field contract mirrors app_smileseasons' DocumentGenerator (the CRM that
// edits these same Xano `billing` rows):
//   • line total ("price × unit") = b.priceXunit when present, else
//     (b.price ?? b.unit_price) × (b.unit ?? b.quantity ?? b.qty ?? 1)
//   • values can arrive as comma-formatted strings ("1,200") → num() strips them.

export function num(v) {
  if (v == null) return 0
  if (typeof v === 'number') return isFinite(v) ? v : 0
  const n = parseFloat(String(v).replace(/[, ]/g, ''))
  return isNaN(n) ? 0 : n
}

/** One billing line's patient-facing amount (price × unit). */
export function lineTotal(b) {
  if (!b) return 0
  if (b.priceXunit != null && b.priceXunit !== '') return num(b.priceXunit)
  const price = num(b.price ?? b.unit_price ?? 0)
  const unit  = num(b.unit ?? b.quantity ?? b.qty ?? 1) || 1
  return price * unit
}

/** One billing line's ACTUAL total (post-insurance) — the figure the SSDC
 *  revenue aggregate (price_actualTotal) sums. Falls back to the price×unit
 *  line total when the dashboard feed doesn't carry priceActualTotal. */
export function lineActualTotal(b) {
  if (!b) return 0
  if (b.priceActualTotal != null && b.priceActualTotal !== '') return num(b.priceActualTotal)
  return lineTotal(b)
}

/** One billing line's clinic share of the actual total (price_actualClinic).
 *  0 when the feed doesn't provide it. */
export function lineActualClinic(b) {
  return num(b?.priceActualClinic)
}

/** Sum of every billing line on a single treatment row. */
export function treatmentTotal(tx) {
  let t = 0
  for (const b of tx?.billing || []) t += lineTotal(b)
  return t
}

/** Sum across a list of treatment rows (skips inactive/voided rows). */
export function rowsIncome(rows) {
  let t = 0
  for (const tx of rows || []) {
    if (tx?.active === false) continue
    t += treatmentTotal(tx)
  }
  return t
}

/** Thai-baht formatting. `compact` uses K/M suffixes for tight spaces. */
export function thb(n, { compact = false } = {}) {
  const v = num(n)
  if (compact) {
    const abs = Math.abs(v)
    if (abs >= 1_000_000) return `฿${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
    if (abs >= 1_000)     return `฿${(v / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}K`
    return `฿${Math.round(v).toLocaleString()}`
  }
  return `฿${Math.round(v).toLocaleString()}`
}
